import { useState } from 'react';
import type { CondimentAdminDTO, SaveCondimentRequest } from '@six-pockets/shared';
import { MoneyInput, TextField } from '@/components/ui';
import { parseWhole } from '../draft';
import { FormRow, ManageFormModal } from './ManageFormModal';

interface CondimentFormModalProps {
  /** Null to add a new condiment. */
  condiment: CondimentAdminDTO | null;
  busy: boolean;
  error: string | null;
  onSave: (body: SaveCondimentRequest) => void;
  onClose: () => void;
}

/** Add or edit a condiment. Mount it only while open (with a key per condiment) so the fields start fresh. */
export function CondimentFormModal({ condiment, busy, error, onSave, onClose }: CondimentFormModalProps) {
  const [name, setName] = useState(condiment?.name ?? '');
  const [cost, setCost] = useState(condiment ? String(condiment.costPrice) : '');
  const [selling, setSelling] = useState(condiment ? String(condiment.sellingPrice) : '');
  const [order, setOrder] = useState(condiment ? String(condiment.sortOrder) : '');

  const costPrice = parseWhole(cost);
  const sellingPrice = parseWhole(selling);
  const sortOrder = order.trim() ? parseWhole(order) : undefined;
  const blocked = !name.trim()
    ? 'Enter a name'
    : costPrice === null
      ? 'Enter the cost price in whole rupees'
      : sellingPrice === null
        ? 'Enter the selling price in whole rupees'
        : sortOrder === null
          ? 'Display order must be a whole number'
          : undefined;

  const submit = () =>
    onSave({
      name: name.trim(),
      costPrice: costPrice!,
      sellingPrice: sellingPrice!,
      ...(sortOrder !== undefined && { sortOrder: sortOrder! }),
    });

  return (
    <ManageFormModal
      title={condiment ? `Edit ${condiment.name}` : 'Add condiment'}
      submitLabel={condiment ? 'Save changes' : 'Add condiment'}
      busy={busy}
      error={error}
      blockedReason={blocked}
      onSubmit={submit}
      onClose={onClose}
    >
      <TextField label="Name" placeholder="Lemon soda" value={name} onChange={(e) => setName(e.target.value)} maxLength={40} data-autofocus />
      <FormRow>
        <MoneyInput label="Cost price" value={cost} onChange={setCost} />
        <MoneyInput label="Selling price" value={selling} onChange={setSelling} />
      </FormRow>
      <TextField
        label="Display order"
        type="number"
        inputMode="numeric"
        min={0}
        step={1}
        mono
        placeholder={condiment ? '' : 'Last'}
        value={order}
        onChange={(e) => setOrder(e.target.value)}
      />
    </ManageFormModal>
  );
}
